import { useMemo } from "react";
import type { DepositRequired } from "../../../common/jsonRpcTypes";
import type { PreorderSelections } from "../bookingForm/BookingForm";

/**
 * Handle tax exclusivity logic and label determination.
 * Extracted from PaymentStep to reduce cognitive complexity.
 */
export const useTaxConfiguration = (
  depositRequired: DepositRequired | null | undefined,
  preorderSelections: PreorderSelections | null,
  isAuthOnly: boolean,
) => {
  return useMemo(() => {
    const isDepositTaxExclusive =
      depositRequired?.tax_inclusive === false &&
      Array.isArray(depositRequired?.formatted_tax_rates) &&
      depositRequired.formatted_tax_rates.length > 0;

    const isPreorderTaxExclusive =
      preorderSelections?.taxInclusive === false &&
      Array.isArray(preorderSelections?.taxBreakdown) &&
      preorderSelections.taxBreakdown.length > 0;

    // Determine the effective tax-exclusive state: deposit takes precedence when present
    // and actually charging (non-auth), otherwise fall back to the preorder's tax configuration.
    const hasChargeableDeposit = !!depositRequired && !isAuthOnly;
    const isEffectiveTaxExclusive = hasChargeableDeposit ? isDepositTaxExclusive : isPreorderTaxExclusive;
    const effectiveTaxLabel = hasChargeableDeposit
      ? (depositRequired.tax_label ?? "VAT")
      : (preorderSelections?.taxLabel ?? "VAT");

    return {
      isDepositTaxExclusive,
      isPreorderTaxExclusive,
      isEffectiveTaxExclusive,
      effectiveTaxLabel,
    };
  }, [depositRequired, isAuthOnly, preorderSelections]);
};
